<template>
  <div>
    <div id="hot"></div>
  </div>
</template>

<script>
import axios from "axios";
import config from '@/config/config';
import Handsontable from "handsontable";
import 'handsontable/dist/handsontable.full.css';

export default {
  name: 'PVDataTable',
  props: {
    load_data_action: {
      type: String,
      required: true
    },
    save_data_action: {
      type: String,
      required: true
    },
    selectedWeek: {
      type: Number,
      required: true
    },
    selectedYear: {
      type: Number,
      required: true
    },
    searchQuery: {
      type: String,
      default: ''
    },
    hideNonMatchingRows: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hotSettings: {
        licenseKey: 'non-commercial-and-evaluation',
        multiColumnSorting: {
          indicator: true,
          headerAction: true,
          sortEmptyCells: true
        },
        hiddenRows: {
          indicators: false
        },
        manualColumnResize: false,
        contextMenu: false
      },
      data: [],
      columnConfig: [],
      columnTitles: [],
      hotInstance: null,
      sortOrder: null,
      currentSortConfig: null,
      sumRow: null,
      fixedColumnsStart: 0,
      loadingFinished: false
    };
  },
  created() {
    this.fetchData(this.load_data_action);
  },
  watch: {
    selectedWeek(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchData(this.load_data_action);
      }
    },
    selectedYear(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchData(this.load_data_action);
      }
    },
    searchQuery(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (!newVal) {
          this.clearSearch();
        } else {
          this.performSearch();
        }
      }
    },
    hideNonMatchingRows() {
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        this.performSearch();
      }
    },
    '$route'(to, from) {
      if (to.path !== from.path) {
        // Route has changed
        if (this.hotInstance) {
          this.hotInstance.destroy();
          this.hotInstance = null;
        }
        // Fetch new data for the new route
        this.fetchData(this.load_data_action);
      }
    }
  },
  methods: {
    async fetchData(action) {
      const url = `${config.BASE_URL}?action=${action}`;
      const params = new URLSearchParams();
      if (this.selectedWeek) {
        params.append('week', this.selectedWeek);
      }
      if (this.selectedYear) {
        params.append('year', this.selectedYear);
      }

      if (this.hotInstance) {
        const sortPlugin = this.hotInstance.getPlugin('multiColumnSorting');
        if (sortPlugin && typeof sortPlugin.getSortConfig === 'function') {
          try {
            this.currentSortConfig = sortPlugin.getSortConfig();
          } catch (e) {
            console.warn('Sort config error:', e);
            this.currentSortConfig = null;
          }
        }
      }

      try {
        const response = await axios.get(`${url}&${params.toString()}`);

        if (response.data.success && response.data.data.data.length > 0) {
          this.storeFetchedData(response.data.data);
          this.initializeOrUpdateHandsontable();
        } else {
          // Handle the case when there is no data
          this.data = [];
          this.columnConfig = [];
          this.columnTitles = [];
          this.sortOrder = null;

          if (this.hotInstance) {
            this.hotInstance.destroy();
            this.hotInstance = null;
          }

          this.$root.showToast('error', config.MESSAGES.NO_DATA_ALLOWED_ERROR);
        }
      } catch (error) {
        this.$root.showToast('error', config.MESSAGES.LOAD_ERROR + error);
      }
      this.loadingFinished = true;
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        this.performSearch();
      }
    },
    storeFetchedData(responseData) {
      this.data = responseData.data;
      this.columnConfig = responseData.columnConfig;
      this.columnTitles = responseData.columnTitles;
      this.sortOrder = responseData.sortOrder;
      this.sumRow = responseData.sumRow;
      this.fixedColumns = responseData.fixedColumns || 0;
    },
    initializeOrUpdateHandsontable() {
      const container = this.$el.querySelector('#hot');

      if (this.sumRow) {
        const sumRow = { ...this.sumRow, entryid: 'Summe', isSumRow: true };
        this.data.push(sumRow);
      }

      const handsontableSettings = {
        data: this.data,
        columns: this.setupColumns(),
        colHeaders: this.columnTitles,
        fixedColumnsStart: this.fixedColumns || 0,
        search: true,
        ...this.hotSettings,
        afterColumnSort: this.afterColumnSort,
        afterChange: this.handleChanges,
        cells: this.applyCellProperties,
        preventOverflow: 'horizontal' // Ensure horizontal overflow is contained
      };

      if (this.hotInstance) {
        // Update the existing Handsontable instance
        this.hotInstance.updateSettings(handsontableSettings);
      } else {
        // Create a new Handsontable instance
        this.hotInstance = new Handsontable(container, handsontableSettings);
      }

      // Apply sorting if a previous sort order exists
      if (this.sortOrder) {
        const columnIndex = this.columnTitles.indexOf(this.sortOrder.column_name);
        if (columnIndex !== -1) {
          this.hotInstance.getPlugin('multiColumnSorting').sort({
            column: columnIndex,
            sortOrder: this.sortOrder.order
          });
        }
      }

      // Reapply captured sort configuration
      if (this.currentSortConfig) {
        this.hotInstance.getPlugin('multiColumnSorting').sort(this.currentSortConfig);
      }
    },
    setupColumns() {
      return this.columnConfig.map(col => {
        const baseColSettings = {
          ...col,
          className: 'htCenter', // Apply center alignment class to all columns
          sorting: true
        };

        if (col.type === 'currency') {
          return {
            ...baseColSettings,
            type: 'numeric',
            renderer: this.currencyRenderer
          };
        } else if (col.type === 'numeric') {
          return {
            ...baseColSettings,
            type: 'numeric',
            renderer: this.numericRenderer
          };
        } else if (col.type === 'date') {
          return {
            ...baseColSettings,
            type: 'date', // Sorting works on the raw value
            renderer: this.dateRenderer, // Display formatted date
            dateFormat: 'YYYY-MM-DD', // Raw date format
            correctFormat: true, // Ensure valid dates
            allowInvalid: false, // Prevent invalid dates
          };
        } else if (col.type === 'time') {  // Use the custom timeRenderer for time columns
          return {
            ...baseColSettings,
            renderer: this.timeRenderer
          };
        } else if (col.type === 'hour') {
          return {
            ...baseColSettings,
            type: 'numeric',
            renderer: this.hourRenderer
          };
        } else {
          return {
            ...baseColSettings,
            renderer: this.textRenderer
          };
        }
      });
    },
    afterColumnSort() {
      if (!this.hotInstance) {
        return;
      }

      const physicalSumRowIndex = this.data.findIndex(row => row.isSumRow); // Find the sum row in source data
      if (physicalSumRowIndex === -1) {
        return;
      }

      const visualSumRowIndex = this.hotInstance.toVisualRow(physicalSumRowIndex); // Map to visual index
      const lastVisualRowIndex = this.hotInstance.countRows() - 1; // Get the last visual row index

      if (visualSumRowIndex !== lastVisualRowIndex) {
        // Move the sum row to the last visual index
        this.hotInstance.rowIndexMapper.moveIndexes(visualSumRowIndex, lastVisualRowIndex);
      }
    },
    getColor(row, col) {
      const column = this.columnConfig[col];
      const cellValue = this.data[row][column.data];
      if (column.negativeBackground && (cellValue < 0 || cellValue === '')) {
        return '#FFCCCC';
      }
      return column.color || 'white';
    },
    currencyRenderer(instance, td, row, col, prop, value, cellProperties) {
      // Check if the value is null, undefined, or not a number
      if (value === null || value === undefined || isNaN(parseFloat(value))) {
        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, '', cellProperties]);
      } else {
        const formatter = new Intl.NumberFormat('de-DE', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const formattedValue = formatter.format(parseFloat(value));
        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, formattedValue, cellProperties]);
      }

      td.style.backgroundColor = cellProperties.color || 'yellow'; // Custom color for currency cells
    },
    numericRenderer(instance, td, row, col, prop, value, cellProperties) {
      // Check if the value is null, undefined, or not a number
      if (value === null || value === undefined || isNaN(parseFloat(value))) {
        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, '', cellProperties]);
      } else {
        // Convert the value to a float
        const numericValue = parseFloat(value);

        // Format the value based on whether it is an integer or a float
        let formattedValue = numericValue;

        // If it's a float, format using German locale settings
        if (!Number.isInteger(numericValue)) {
          formattedValue = numericValue.toLocaleString('de-DE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          });
        }

        // Apply the formatted value to the cell
        Handsontable.renderers.NumericRenderer.apply(this, [instance, td, row, col, prop, formattedValue, cellProperties]);
      }

      td.style.backgroundColor = cellProperties.color || 'white'; // Apply background color based on cell properties
    },
    textRenderer(instance, td, row, col, prop, value, cellProperties) {
      Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, value, cellProperties]);
      td.style.backgroundColor = cellProperties.color || 'white';  // Default color for text cells
    },
    dateRenderer(instance, td, row, col, prop, value, cellProperties) {
      if (value) {
        // Parse the value (expected raw format: YYYY-MM-DD)
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          // Format the date to d.m.Y
          const formattedDate = date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, formattedDate, cellProperties]);
        } else {
          Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, value, cellProperties]);
        }
      } else {
        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, '', cellProperties]);
      }

      // Optionally set custom cell styles
      td.style.backgroundColor = cellProperties.color || 'white';
    },
    timeRenderer(instance, td, row, col, prop, value, cellProperties) {
      if (value) {
        // Parse the time string to a Date object
        let [hours, minutes, seconds] = value.split(':').map(Number);
        let ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12; // Convert to 12-hour format

        // Format the time string to hh:mm:ss AM/PM
        const formattedValue = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} ${ampm}`;
        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, formattedValue, cellProperties]);
      } else {
        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, '', cellProperties]);
      }

      td.style.backgroundColor = cellProperties.color || 'white'; // Default color for time cells
    },
    hourRenderer(instance, td, row, col, prop, value, cellProperties) {
      if (value === null || value === undefined || isNaN(parseFloat(value))) {
        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, '', cellProperties]);
      } else {
        const numericValue = parseFloat(value);
        let formattedValue = numericValue.toLocaleString('de-DE', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }) + " Std";  // Append " Std" to the numeric value

        Handsontable.renderers.TextRenderer.apply(this, [instance, td, row, col, prop, formattedValue, cellProperties]);
      }
      td.style.backgroundColor = cellProperties.color || 'white';
    },
    applyCellProperties(row, col) {
      const cellProperties = {};
      const rowData = this.data[row]; // Access the row's data directly from this.data

      if (rowData?.isSumRow) {
        // Sum row properties
        cellProperties.readOnly = true; // Make sum row cells read-only
        cellProperties.className = 'htSumRow'; // Add a specific class for styling
      } else {
        // Normal row properties
        cellProperties.color = this.getColor(row, col);
      }

      return cellProperties;
    },
    handleChanges(changes, source) {
      if (source !== 'loadData' && changes) {
        const changesWithId = changes.map(change => {
          const rowIndex = change[0];
          const physicalRow = this.hotInstance.toPhysicalRow(rowIndex); // Translate visual to physical index
          const column = this.columnConfig.find(column => column.data === change[1]);
          const newValue = column.type === 'checkbox' ? (change[3] ? 'x' : null) : change[3];

          return [
            this.data[physicalRow].entryid, // Use physical index to access the correct data row
            column.data,
            change[2],
            newValue
          ];
        });
        this.saveData(changesWithId);
        if (this.searchQuery && this.searchQuery.trim() !== '') {
          this.performSearch();
        }
      }
    },
    async saveData(changes) {
      try {
        const params = new URLSearchParams();
        params.append('changes', JSON.stringify(changes));
        const url = `${config.BASE_URL}?action=${this.save_data_action}`;
        const response = await axios.post(url, params);
        if (!response.data.success) {
          this.$root.showToast('error', config.MESSAGES.SAVE_ERROR + response.data.data);
        } else {
          this.$root.showToast('success', config.MESSAGES.SAVE_SUCCESS);
          this.fetchData(this.load_data_action);
        }
      } catch (error) {
        this.$root.showToast('error', config.MESSAGES.SEND_ERROR + error);
      }
    },
    performSearch() {
      const searchPlugin = this.hotInstance.getPlugin('search');
      const results = searchPlugin.query(this.searchQuery);
      this.hotInstance.render();

      const hiddenRowsPlugin = this.hotInstance.getPlugin('hiddenRows');
      const allRowIndices = this.data.map((_, idx) => idx);

      if (this.hideNonMatchingRows) {
        const matchedRows = new Set(results.map(result => result.row));
        const rowsToHide = allRowIndices.filter(index => !matchedRows.has(index));
        hiddenRowsPlugin.hideRows(rowsToHide);
      } else {
        hiddenRowsPlugin.showRows(allRowIndices);
      }

      this.hotInstance.render();

      if (results.length > 0) {
        const firstResult = results[0];
        this.hotInstance.selectCell(firstResult.row, firstResult.col);
      }
    },
    clearSearch() {
      if (!this.hotInstance) return;

      // Clear search highlights
      const searchPlugin = this.hotInstance.getPlugin('search');
      searchPlugin.query('');

      // Show all rows
      const plugin = this.hotInstance.getPlugin('hiddenRows');
      plugin.showRows([...Array(this.data.length).keys()]);

      this.hotInstance.render();
    }
  }
}
</script>

<style>
/* The existing styles */
.handsontable .htSearchResult {
  background-color: #FFFF00 !important; /* Highlight color for search matches */
}
.htSumRow {
  background-color: #f9f9f9 !important;
  font-weight: bold;
  color: #000 !important;
  text-align: center !important;
  border-top: 2px solid #000 !important;
}
</style>
